import React, { FC, useEffect, useState } from "react";

import clsx from "clsx";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Checkbox, FormControlLabel } from "@mui/material";
import Link from "@mui/material/Link";

import { StyledCustomerTypography } from "@/components/features/ToDos/CustomerCardPage/styled";
import { InfoBox } from "@/components/molecules";
import { FormPanel, StepComponentProps, TermsOfUseDialog } from "@/components/organisms";
import config from "@/config";

import { CustomerCardFormType } from "../../formType";

const PreferenceStep: FC<StepComponentProps> = () => {
    const { t } = useTranslation();

    const {
        setValue,
        watch,
        control,
        trigger,
        formState: { errors },
    } = useFormContext<CustomerCardFormType>();

    const [termsState, setTermsState] = useState(false);
    const [showWarnings, setShowWarnings] = useState(false);

    const medicalInfo = watch("preferences.medicalInfo");

    const onClickTerms = () => {
        setTermsState(true);
    };

    const handleCloseTerms = () => {
        setTermsState(false);
    };

    const errorBypassed = errors?.preferences?.medicalWarningBypassed;
    const errorMedicalInfo = errors?.preferences?.medicalInfo || showWarnings;

    useEffect(() => {
        if (errorBypassed) {
            setValue("preferences.medicalWarningBypassed", true, { shouldValidate: false });
            setShowWarnings(true);
        }
    }, [errorBypassed]);

    useEffect(() => {
        setValue("preferences.medicalWarningBypassed", medicalInfo, { shouldValidate: medicalInfo });
        if (medicalInfo) {
            trigger("preferences.medicalInfo");
            setShowWarnings(false);
        }
    }, [medicalInfo]);

    return (
        <>
            {termsState && <TermsOfUseDialog onClose={handleCloseTerms} type="customerTermsOfUse" />}
            {showWarnings && (
                <InfoBox
                    type="error"
                    onClose={() => setShowWarnings(false)}
                    style={{ marginBottom: "16px" }}
                    data-testid="medical-warning"
                >
                    {t("CustomerCard.MedicalProcessingWarningMessage")}
                </InfoBox>
            )}
            <FormPanel title={t("CustomerCard.General.UsagePreference")} mode="edit">
                <StyledCustomerTypography>
                    {t("CustomerCard.UsagePreference.PersonalProtectionPolicy")}
                </StyledCustomerTypography>
                <StyledCustomerTypography>
                    {t("CustomerCard.UsagePreference.PersonalProtectionPolicyMessage")}
                </StyledCustomerTypography>
                <StyledCustomerTypography>
                    <Controller
                        control={control}
                        name="preferences.commercialInfo"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="checkbox"
                                        inputProps={{
                                            // @ts-ignore
                                            "data-testid": "checkbox-commercialInfo",
                                        }}
                                        color="primary"
                                        checked={value}
                                        onChange={(e) => onChange(e.target.checked)}
                                        onBlur={onBlur}
                                    />
                                }
                                label={t("CustomerCard.UsagePreference.AllowCommercialInfo")}
                                labelPlacement="end"
                            />
                        )}
                    />
                </StyledCustomerTypography>
                <StyledCustomerTypography>
                    {t("CustomerCard.UsagePreference.FindMoreInfo")}
                    <Link onClick={onClickTerms}>{t("Common.GeneralConditions")}</Link>
                </StyledCustomerTypography>
                <StyledCustomerTypography>
                    {t("CustomerCard.UsagePreference.ProcessingPersonalData")}
                </StyledCustomerTypography>
                <StyledCustomerTypography>
                    <Controller
                        control={control}
                        name="preferences.medicalInfo"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        inputProps={{
                                            // @ts-ignore
                                            "data-testid": "checkbox-medicalInfo",
                                        }}
                                        name="checkbox"
                                        color="primary"
                                        className={clsx("checkboxInput", errorMedicalInfo && "error")}
                                        checked={value}
                                        onChange={(e) => onChange(e.target.checked)}
                                        onBlur={onBlur}
                                    />
                                }
                                label={
                                    <span className={clsx(errorMedicalInfo && "error")}>
                                        {t("CustomerCard.UsagePreference.ProcessingMedicalData")}
                                    </span>
                                }
                                labelPlacement="end"
                            />
                        )}
                    />{" "}
                </StyledCustomerTypography>

                <StyledCustomerTypography>
                    <Controller
                        control={control}
                        name="preferences.securityInfo"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        inputProps={{
                                            // @ts-ignore
                                            "data-testid": "checkbox-securityInfo",
                                        }}
                                        name="checkbox"
                                        color="primary"
                                        className="checkboxInput"
                                        checked={value}
                                        onChange={(e) => onChange(e.target.checked)}
                                        onBlur={onBlur}
                                    />
                                }
                                label={t("CustomerCard.UsagePreference.ProcessingCriminalData")}
                                labelPlacement="end"
                            />
                        )}
                    />
                </StyledCustomerTypography>

                <StyledCustomerTypography>
                    {t("CustomerCard.UsagePreference.SubjectToCompliance")} {" - "}
                    <Link href={config.DATA_PROTECTION_AUTHORITY_URL} target="_blank" rel="noreferrer">
                        {config.DATA_PROTECTION_AUTHORITY_URL}
                    </Link>
                    {" - "}
                    <Link href={`mailto:${config.DATA_PROTECTION_AUTHORITY_EMAIL}`}>
                        {config.DATA_PROTECTION_AUTHORITY_EMAIL}
                    </Link>
                </StyledCustomerTypography>
                <StyledCustomerTypography>{t("CustomerCard.UsagePreference.Accessibility")}</StyledCustomerTypography>
                <StyledCustomerTypography>{t("CustomerCard.UsagePreference.Availability")}</StyledCustomerTypography>
            </FormPanel>
        </>
    );
};

export default PreferenceStep;
